import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar, Download, ExternalLink } from "lucide-react";

const AddToCalendarModal = ({ isOpen, onClose, job }) => {
  const [loading, setLoading] = useState(false);

  if (!job) return null;

  const handleGoogleCalendar = async () => {
    try {
      const response = await fetch(`/api/jobs/${job.job_id}/calendar/links`);
      const data = await response.json();
      window.open(data.links.google, '_blank');
    } catch (error) {
      console.error("Failed to get calendar link:", error);
    }
  };

  const handleOutlookCalendar = async () => {
    try {
      const response = await fetch(`/api/jobs/${job.job_id}/calendar/links`);
      const data = await response.json();
      window.open(data.links.outlook, '_blank');
    } catch (error) {
      console.error("Failed to get calendar link:", error);
    }
  };

  const handleYahooCalendar = async () => {
    try {
      const response = await fetch(`/api/jobs/${job.job_id}/calendar/links`);
      const data = await response.json();
      window.open(data.links.yahoo, '_blank');
    } catch (error) {
      console.error("Failed to get calendar link:", error);
    }
  };

  const handleDownloadICS = () => {
    window.open(`/api/jobs/${job.job_id}/calendar/ics`, '_blank');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border-[#27272A] text-white max-w-md" aria-describedby="calendar-modal-description">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#39FF14]" />
            Add to Calendar
          </DialogTitle>
          <p id="calendar-modal-description" className="sr-only">
            Choose a calendar service to add your lawn care appointment
          </p>
        </DialogHeader>

        <div className="space-y-3">
          {/* Google Calendar */}
          <button
            onClick={handleGoogleCalendar}
            className="w-full flex items-center gap-4 p-4 bg-[#121212] border border-[#27272A] rounded-xl hover:border-[#39FF14]/50 transition-colors"
          >
            <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-8 h-8">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </div>
            <div className="flex-1 text-left">
              <h4 className="font-semibold text-white">Google Calendar</h4>
              <p className="text-[#A1A1AA] text-sm">Add to your Google Calendar</p>
            </div>
            <ExternalLink className="w-5 h-5 text-[#A1A1AA]" />
          </button>

          {/* Apple Calendar / iCal */}
          <button
            onClick={handleDownloadICS}
            className="w-full flex items-center gap-4 p-4 bg-[#121212] border border-[#27272A] rounded-xl hover:border-[#39FF14]/50 transition-colors"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center">
              <Calendar className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1 text-left">
              <h4 className="font-semibold text-white">Apple Calendar</h4>
              <p className="text-[#A1A1AA] text-sm">Download .ics file for iPhone/Mac</p>
            </div>
            <Download className="w-5 h-5 text-[#A1A1AA]" />
          </button>

          {/* Outlook */}
          <button
            onClick={handleOutlookCalendar}
            className="w-full flex items-center gap-4 p-4 bg-[#121212] border border-[#27272A] rounded-xl hover:border-[#39FF14]/50 transition-colors"
          >
            <div className="w-12 h-12 bg-[#0078D4] rounded-lg flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-7 h-7" fill="white">
                <path d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V12zm-6-8.25v3h3v-3zm0 4.5v3h3v-3zm0 4.5v1.83l3.05-1.83zm-5.25-9v3h3.75v-3zm0 4.5v3h3.75v-3zm0 4.5v2.03l2.41 1.5 1.34-.8v-2.73zM9 3.75V6h2l.13.01.12.04v-2.3zM5.98 15.98q.9 0 1.6-.3.7-.32 1.19-.86.48-.55.73-1.28.25-.74.25-1.61 0-.83-.25-1.55-.24-.71-.71-1.24t-1.15-.83q-.68-.3-1.55-.3-.92 0-1.64.3-.71.3-1.2.85-.5.54-.75 1.3-.25.74-.25 1.63 0 .85.26 1.56.26.72.74 1.23.48.52 1.17.81.69.3 1.56.3zM7.5 21h12.39L12 16.08V17q0 .41-.3.7-.29.3-.7.3H7.5zm15-.13v-7.24l-5.9 3.54Z"/>
              </svg>
            </div>
            <div className="flex-1 text-left">
              <h4 className="font-semibold text-white">Outlook</h4>
              <p className="text-[#A1A1AA] text-sm">Add to Outlook Calendar</p>
            </div>
            <ExternalLink className="w-5 h-5 text-[#A1A1AA]" />
          </button>

          {/* Yahoo */}
          <button
            onClick={handleYahooCalendar}
            className="w-full flex items-center gap-4 p-4 bg-[#121212] border border-[#27272A] rounded-xl hover:border-[#39FF14]/50 transition-colors"
          >
            <div className="w-12 h-12 bg-[#6001D2] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">Y!</span>
            </div>
            <div className="flex-1 text-left">
              <h4 className="font-semibold text-white">Yahoo Calendar</h4>
              <p className="text-[#A1A1AA] text-sm">Add to Yahoo Calendar</p>
            </div>
            <ExternalLink className="w-5 h-5 text-[#A1A1AA]" />
          </button>

          {/* Download ICS for other apps */}
          <div className="pt-4 border-t border-[#27272A]">
            <button
              onClick={handleDownloadICS}
              className="w-full flex items-center justify-center gap-2 p-3 text-[#39FF14] hover:bg-[#39FF14]/10 rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              <span className="text-sm">Download .ics file (works with any calendar app)</span>
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddToCalendarModal;
